import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, FileText, Plus, StickyNote } from 'lucide-react';

interface KnowledgeAddMenuProps {
  isAdmin: boolean;
  onAddSource: (scope: 'user' | 'tenant') => void;
  onAddNote: (scope: 'user' | 'tenant') => void;
}

export const KnowledgeAddMenu = ({
  isAdmin,
  onAddSource,
  onAddNote,
}: KnowledgeAddMenuProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1.5 rounded-lg bg-[#252F33] text-white text-sm font-medium px-3.5 py-2 shadow-sm hover:bg-[#1a2226] hover:shadow-md transition-shadow">
        <Plus className="h-3.5 w-3.5" />
        Add
        <ChevronDown className="h-3.5 w-3.5 opacity-80" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-20 min-w-[240px] rounded-lg border border-[#E4E9EC] bg-white py-1 shadow-lg">
          <button
            type="button"
            onClick={() => pick(() => onAddSource('user'))}
            className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-[#FAFBFC]">
            <FileText className="h-4 w-4 mt-0.5 text-[#007CA0]" />
            <span>
              <span className="block text-sm font-medium text-[#252F33]">
                Add source
              </span>
              <span className="block text-xs text-[#768D95] mt-0.5">
                Upload a text document for cited answers
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => pick(() => onAddNote('user'))}
            className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-[#FAFBFC]">
            <StickyNote className="h-4 w-4 mt-0.5 text-[#007CA0]" />
            <span>
              <span className="block text-sm font-medium text-[#252F33]">
                Add note
              </span>
              <span className="block text-xs text-[#768D95] mt-0.5">
                Short typed context you write yourself
              </span>
            </span>
          </button>
          {isAdmin && (
            <>
              <div className="my-1 h-px bg-[#EDF1F3]" />
              <button
                type="button"
                onClick={() => pick(() => onAddSource('tenant'))}
                className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-[#FAFBFC]">
                <FileText className="h-4 w-4 mt-0.5 text-[#007CA0]" />
                <span>
                  <span className="block text-sm font-medium text-[#252F33]">
                    Share a source
                  </span>
                  <span className="block text-xs text-[#768D95] mt-0.5">
                    Team-wide document upload
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => pick(() => onAddNote('tenant'))}
                className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-[#FAFBFC]">
                <StickyNote className="h-4 w-4 mt-0.5 text-[#007CA0]" />
                <span>
                  <span className="block text-sm font-medium text-[#252F33]">
                    Publish a note
                  </span>
                  <span className="block text-xs text-[#768D95] mt-0.5">
                    Shared with everyone on your team
                  </span>
                </span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
